import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'vangard.session';

// The site and the API live on different domains in production, so the session
// cookie is a third-party cookie that mobile browsers drop. Keep the session
// token client-side as well and send it as a bearer token.
const readToken = () => {
  try { return window.localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
};

const applyToken = (token) => {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    try { window.localStorage.setItem(TOKEN_KEY, token); } catch { /* private mode */ }
  } else {
    delete axios.defaults.headers.common.Authorization;
    try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ }
  }
};

axios.defaults.withCredentials = true;
applyToken(readToken());

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Check auth status on mount
  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401) applyToken('');
      // Don't blow away a user that register()/login() already set —
      // only clear if nothing was set yet.
      setUser((prev) => prev || null);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const adopt = (data) => {
    if (data?.session_token) applyToken(data.session_token);
    setUser(data);
    return data;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    return adopt(response.data);
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    return adopt(response.data);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogleCredential = async (credential) => {
    const response = await axios.post(`${API}/auth/google`,
      { credential },
      { withCredentials: true }
    );
    return adopt(response.data);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    applyToken('');
    setUser(null);
  };

  const value = {
    user,
    loading,
    initialized,
    login,
    register,
    loginWithGoogleCredential,
    logout,
    setUser,
    refreshUser: checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
